import React, { memo, useEffect, useCallback } from 'react';
import { Text } from '@cuemath/web-comps';
import COLORS from 'constants/colors';
import useVideoPlayer, { PLAYBACK_RATES } from '../../hooks/use-video-player';
import {
  ViewerContainer, Overlay, Video, Image,
  BufferingOverlay, ProgressBarTrack, ProgressBarFill,
  SpeedControls, SpeedButton,
} from './evidence-viewer-styled';

const EvidenceViewer = ({ chunk, overlayText, onPlay, nextVideoUrl }) => {
  const evidenceUrl = chunk?.evidence_url;
  const isVideo = chunk?.evidence_type === 'video';
  const isViewed = chunk?.is_viewed;
  const currentUrl = isVideo ? evidenceUrl : null;

  const {
    videoProps,
    isBuffering,
    progress,
    showLoading,
    hasError,
    playbackRate,
    setPlaybackRate,
  } = useVideoPlayer(currentUrl, nextVideoUrl);

  const handlePlay = useCallback(() => {
    if (onPlay && !isViewed) onPlay();
  }, [onPlay, isViewed]);

  // Auto-mark image chunks as viewed
  useEffect(() => {
    if (chunk && !isVideo && !isViewed && onPlay) onPlay();
  }, [chunk, isVideo, isViewed, onPlay]);

  if (!chunk) return null;

  const loadingMessage = isBuffering
    ? `Loading video${progress > 0 ? ` ${Math.round(progress * 100)}%` : '...'}`
    : 'Preparing video...';

  return (
    <ViewerContainer>
      {overlayText && (
        <Overlay>
          <Text variant="body3" color={COLORS.WHITE}>{overlayText}</Text>
        </Overlay>
      )}
      {isVideo ? (
        <>
          {showLoading && (
            <BufferingOverlay justifyContent="center" alignItems="center">
              <Text variant="body2" color={COLORS.WHITE}>
                {hasError ? 'Failed to load video. Please refresh to retry.' : loadingMessage}
              </Text>
              {isBuffering && !hasError && (
                <ProgressBarTrack>
                  <ProgressBarFill percent={(progress || 0) * 100} />
                </ProgressBarTrack>
              )}
            </BufferingOverlay>
          )}
          {!showLoading && (
            <SpeedControls flexDirection="row" alignItems="center">
              {PLAYBACK_RATES.map(rate => (
                <SpeedButton
                  key={rate}
                  isActive={playbackRate === rate}
                  onClick={() => setPlaybackRate(rate)}
                >
                  <Text variant="body2" color={playbackRate === rate ? COLORS.WHITE : COLORS.WHITE_T_60}>
                    {rate}x
                  </Text>
                </SpeedButton>
              ))}
            </SpeedControls>
          )}
          <Video {...videoProps} onPlay={handlePlay} />
        </>
      ) : (
        <Image key={evidenceUrl} src={evidenceUrl} alt="Evidence" />
      )}
    </ViewerContainer>
  );
};

export default memo(EvidenceViewer);
